import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, MapPin, Clock, Mail, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Mock form submission - In production, this would send to backend
    console.log('Form submitted:', formData);
    toast.success('Request sent! We\'ll contact you soon.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      vehicle: '',
      service: '',
      message: ''
    });
  };

  return (
    <div className="contact-page">
      <Header />
      <Toaster position="top-right" />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content text-center">
            <h1 className="fade-in-up">Contact Us</h1>
            <p className="contact-hero-subtitle fade-in-up">
              Get in touch for service appointments, quotes, or questions
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section section-dark">
        <div className="container">
          <div className="contact-info-grid">
            <div className="contact-info-card card">
              <Phone size={40} className="contact-card-icon" />
              <h3>Call Us</h3>
              <p>Speak directly with our team</p>
              <a href="tel:6517723388" className="contact-link">(651) 772-3388</a>
            </div>
            
            <div className="contact-info-card card">
              <MapPin size={40} className="contact-card-icon" />
              <h3>Visit Us</h3>
              <p>931 Atlantic St<br />St Paul, MN 55106</p>
              <a 
                href="https://www.google.com/maps/search/?api=1&query=931+Atlantic+St,+St+Paul,+MN+55106" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="contact-link"
              >
                Get Directions
              </a>
            </div>
            
            <div className="contact-info-card card">
              <Clock size={40} className="contact-card-icon" />
              <h3>Hours</h3>
              <div className="hours-list">
                <div className="hours-row">
                  <span>Mon - Fri:</span>
                  <span>9:00 AM - 6:30 PM</span>
                </div>
                <div className="hours-row">
                  <span>Saturday:</span>
                  <span>10:00 AM - 4:30 PM</span>
                </div>
                <div className="hours-row">
                  <span>Sunday:</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section section-light">
        <div className="container">
          <div className="contact-form-section">
            <div className="form-wrapper">
              <h2>Request Service or Quote</h2>
              <p className="form-intro">
                Fill out the form below and we'll get back to you as soon as possible. For immediate assistance, please call us at (651) 772-3388.
              </p>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(651) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="vehicle">Vehicle</label>
                    <input
                      type="text"
                      id="vehicle"
                      name="vehicle"
                      value={formData.vehicle}
                      onChange={handleChange}
                      placeholder="Year, Make, Model"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="service">Service Needed</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                  >
                    <option value="">Select a service</option>
                    <option value="diagnostics">Engine Diagnostics</option>
                    <option value="brakes">Brake Service</option>
                    <option value="suspension">Suspension & Steering</option>
                    <option value="electrical">Electrical Systems</option>
                    <option value="transmission">Transmission Service</option>
                    <option value="body">Body & Bumper Repair</option>
                    <option value="cooling">Cooling System</option>
                    <option value="ac">AC & Heating</option>
                    <option value="maintenance">General Maintenance</option>
                    <option value="engine">Engine Repair</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Please describe your vehicle issue or service needs..."
                  ></textarea>
                </div>

                <button type="submit" className="btn-primary btn-submit">
                  <Send size={20} />
                  Send Request
                </button>
              </form>
            </div>

            <div className="map-wrapper">
              <h3>Find Us</h3>
              <div className="map-container">
                <iframe
                  title="Moua's Auto Repair Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2823.0987654321!2d-93.0612345!3d44.9537!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b32d0e4c2f8b8f%3A0x1234567890abcdef!2s931%20Atlantic%20St%2C%20St%20Paul%2C%20MN%2055106!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="450"
                  style={{ border: 0, borderRadius: '1rem' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
              <div className="map-info">
                <MapPin size={24} className="map-info-icon" />
                <div>
                  <strong>931 Atlantic St</strong>
                  <p>St Paul, MN 55106</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
